import { DEFAULT_CURRENCY_CODE } from '@klear/constants/currency'
import { Money } from '@klear/money'
import toPairs from 'lodash/toPairs'
import values from 'lodash/values'

export const MICRODEPOSIT_MIN = Money.fromMinorUnits(1n, DEFAULT_CURRENCY_CODE)
export const MICRODEPOSIT_MAX = Money.fromMinorUnits(99n, DEFAULT_CURRENCY_CODE)

const MICRODEPOSIT_RANGE_MESSAGE = `Expected an amount between ${MICRODEPOSIT_MIN.toString()} and ${MICRODEPOSIT_MAX.toString()}`

const DESCRIPTOR_CODE_PATTERN = /^SM[A-Z0-9]{4}$/

export type VerifyMicrodepositsForm = {
  amount1?: Money
  amount2?: Money
  descriptorCode?: string
}

export type VerifyMicrodepositsFormErrorMap = Record<string, string | undefined>

export type VerifyMicrodepositsVerificationMethod = 'amounts' | 'descriptor_code' | null

function isMicrodepositAmount(amount: Money | undefined): amount is Money {
  if (amount == null) return false
  if (amount.currency !== DEFAULT_CURRENCY_CODE) return false
  return amount.greaterThanOrEqualTo(MICRODEPOSIT_MIN) && amount.lessThanOrEqualTo(MICRODEPOSIT_MAX)
}

function normalizeVerifyMicrodepositsForm(form: VerifyMicrodepositsForm): VerifyMicrodepositsForm {
  const descriptorCode = form.descriptorCode?.trim()
  return {
    ...form,
    descriptorCode: descriptorCode || undefined
  }
}

function validateAmountsMode(form: VerifyMicrodepositsForm): VerifyMicrodepositsFormErrorMap {
  const errors: VerifyMicrodepositsFormErrorMap = {}

  if (!isMicrodepositAmount(form.amount1)) {
    errors.amount1 = MICRODEPOSIT_RANGE_MESSAGE
  }
  if (!isMicrodepositAmount(form.amount2)) {
    errors.amount2 = MICRODEPOSIT_RANGE_MESSAGE
  }

  return errors
}

function validateDescriptorMode(form: VerifyMicrodepositsForm): VerifyMicrodepositsFormErrorMap {
  const errors: VerifyMicrodepositsFormErrorMap = {}
  const descriptorCode = form.descriptorCode ?? ''

  if (!descriptorCode) {
    errors.descriptorCode = 'Descriptor code is required'
  } else if (!DESCRIPTOR_CODE_PATTERN.test(descriptorCode)) {
    errors.descriptorCode =
      'Descriptor code must match the pattern: SM followed by 4 uppercase letters or numbers (e.g., SM11AA)'
  }

  return errors
}

function validateCombinedMode(form: VerifyMicrodepositsForm): VerifyMicrodepositsFormErrorMap {
  const errors: VerifyMicrodepositsFormErrorMap = {}
  const hasAmount1 = form.amount1 !== undefined
  const hasAmount2 = form.amount2 !== undefined
  const hasDescriptorCode = !!form.descriptorCode

  if (hasAmount1 && !isMicrodepositAmount(form.amount1)) {
    errors.amount1 = MICRODEPOSIT_RANGE_MESSAGE
  }
  if (hasAmount2 && !isMicrodepositAmount(form.amount2)) {
    errors.amount2 = MICRODEPOSIT_RANGE_MESSAGE
  }
  if (hasDescriptorCode && form.descriptorCode && !DESCRIPTOR_CODE_PATTERN.test(form.descriptorCode)) {
    errors.descriptorCode =
      'Descriptor code must match the pattern: SM followed by 4 uppercase letters or numbers (e.g., SM11AA)'
  }

  const hasAmounts = hasAmount1 && hasAmount2
  if (!hasAmounts && !hasDescriptorCode) {
    errors.amount1 = 'You must provide either both microdeposit amounts or a descriptor code'
  }

  return errors
}

export function validateVerifyMicrodepositsForm(
  form: VerifyMicrodepositsForm,
  method: VerifyMicrodepositsVerificationMethod
): VerifyMicrodepositsFormErrorMap {
  const normalized = normalizeVerifyMicrodepositsForm(form)

  if (method === 'amounts') {
    return validateAmountsMode(normalized)
  }

  if (method === 'descriptor_code') {
    return validateDescriptorMode({
      ...normalized,
      descriptorCode: normalized.descriptorCode ?? ''
    })
  }

  return validateCombinedMode(normalized)
}

export function hasVerifyMicrodepositsFormErrors(errors: VerifyMicrodepositsFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

export function verifyMicrodepositsFormFieldErrors(
  errors: VerifyMicrodepositsFormErrorMap
): Record<string, { type: 'manual'; message: string }> {
  const fieldErrors: Record<string, { type: 'manual'; message: string }> = {}
  for (const [key, message] of toPairs(errors)) {
    if (message) {
      fieldErrors[key] = { type: 'manual', message }
    }
  }
  return fieldErrors
}
